import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Button from '@components/ui/Button'
import Badge from '@components/ui/Badge'
import Modal from '@components/ui/Modal'
import Loading from '@components/ui/Loading'
import { 
  Calculator, 
  Calendar, 
  Users, 
  DollarSign, 
  Plus, 
  Trash2, 
  TrendingUp, 
  Save,
  Filter,
  ChevronDown,
  ChevronUp,
  Play,
  CheckCircle2,
  ArrowLeft,
  BarChart3,
  Download,
  FileText,
  Image
} from 'lucide-react'
import { getScenario } from '@services/scenarioService'
import { getLatestMonthSummary } from '@services/payrollService'
import { useToast } from '@hooks/useToast'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import * as htmlToImage from 'html-to-image';
import html2pdf from 'html2pdf.js';
import {getRuleById,} from '@services/ruleService';

// html2canvas e jsPDF são carregados dinamicamente sob demanda (code splitting)

export default function ScenarioDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  // Estados similares ao SimulatorPageNew
  const [baseData, setBaseData] = useState(null)
  const [appliedRules, setAppliedRules] = useState([])
  const [projections, setProjections] = useState([])
  const [discountRate, setDiscountRate] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Dados do cenário
  const [scenario, setScenario] = useState(null)
  const [simulationTotals, setSimulationTotals] = useState(null)

  // Modal de detalhes da regra
  const [showRuleDetailsModal, setShowRuleDetailsModal] = useState(false)
  const [selectedRuleDetails, setSelectedRuleDetails] = useState(null)

  // Carregar cenário
  useEffect(() => {
  const loadScenario = async () => {
    try {
      setLoading(true);

      // 1. Carrega o cenário
      const scenarioData = await getScenario(id);
      setScenario(scenarioData);

      const layout = scenarioData.layout_json || {};

      // 2. Carrega dados base da folha
      if (scenarioData.reference_month) {
        const summary = await getLatestMonthSummary(null, {
          month_year: scenarioData.reference_month,
        });
        const payrollTotal =
          scenarioData.calculation_base === 'gross'
            ? summary.gross_total || 0
            : summary.net_total || 0;

        setBaseData({
          total_employees: summary.employee_count || 0,
          payroll_total: payrollTotal,
        });
      }

      // 3. Carrega as regras aplicadas com detalhes completos
      const applied = layout.applied_rules || [];

      // Busca detalhes de cada regra usando getRuleById
      const detailedApplied = await Promise.all(
        applied.map(async (item) => {
          let fullRule = null;

          // Só tenta buscar se tiver rule_id
          if (item.rule_id) {
            fullRule = await getRuleById(item.rule_id);
          }

          // Se não encontrou a regra ou deu erro, usa fallback
          const ruleFallback = {
            id: item.rule_id || 'sem-id',
            name: item.rule_id ? `Regra ${item.rule_id}` : 'Regra sem ID',
            description: 'Descrição não disponível',
            conditions_json: [], // evita erro ao acessar .conditions_json
          };

          return {
            rule: fullRule || ruleFallback,
            proportion: item.proportion || 0,
            affected_employees: item.affected_employees || 0,
            affected_payroll: item.affected_payroll || 0,
            estimated_increase: item.estimated_increase || 0,
            percentage_impact: item.percentage_impact || 0,
            rule_id: item.rule_id,
          };
        })
      );

      setAppliedRules(detailedApplied);

      // 4. Restante das informações do layout
      setProjections(layout.projections || []);
      setSimulationTotals(layout.simulation_totals || {});

      // 5. Discount rate (pode ser calculado ou vir do layout no futuro)
      setDiscountRate(0.2); // valor fixo por enquanto
      

    } catch (e) {
      console.error('Erro ao carregar cenário:', e);
      setError('Erro ao carregar cenário: ' + (e.message || 'Erro desconhecido'));
      toast.error('Não foi possível carregar o cenário');
    } finally {
      setLoading(false);
    }
  };

    if (id) {
      loadScenario()
    }
  }, [id, toast])

  // Calcular totais (similar ao useMemo)
  const calculatedTotals = useMemo(() => {
    if (!baseData) return null

    const totalIncrease = appliedRules.reduce((sum, r) => sum + (r.estimated_increase || 0), 0)
    const newPayrollTotal = (baseData.payroll_total || 0) + totalIncrease
    const percentageIncrease = (baseData.payroll_total || 0) > 0 ? totalIncrease / baseData.payroll_total * 100 : 0

    return {
      current_payroll: baseData.payroll_total || 0,
      total_increase: totalIncrease,
      new_payroll: newPayrollTotal,
      percentage_increase: percentageIncrease,
      affected_employees: Math.max(...appliedRules.map(r => r.affected_employees || 0), 0)
    }
  }, [baseData, appliedRules])

  // Projeção sem regras: aumento mensal médio + valor final após 12 meses
  const projectionWithoutRules = useMemo(() => {
  // Caso não tenha dados suficientes
  if (!projections || projections.length < 3 || !baseData?.payroll_total) {
    return {
      monthlyIncrease: 0,
      finalValueAfter12Months: baseData?.payroll_total || 0,
      hasEnoughData: false
    };
  }

  // Pegamos apenas os valores históricos reais (antes do salto das regras)
  const historical = projections
    .filter(p => p.historical_payroll > 0) // só meses com dado histórico
    .map(p => p.historical_payroll)
    .sort((a, b) => a - b);  // ordena por valor (para garantir sequência)

  // Precisamos de pelo menos 2 pontos para calcular diferença
  if (historical.length < 2) {
    return {
      monthlyIncrease: 0,
      finalValueAfter12Months: baseData.payroll_total,
      hasEnoughData: false
    };
  }

  // Calcula as diferenças mensais
  const monthlyIncreases = [];
  for (let i = 1; i < historical.length; i++) {
    monthlyIncreases.push(historical[i] - historical[i - 1]);
  }

  // Média do aumento mensal
  const avgMonthlyIncrease = monthlyIncreases.reduce((sum, val) => sum + val, 0) / monthlyIncreases.length;

  // Último valor histórico conhecido
  const lastHistorical = historical[historical.length - 1];

  // Projeção linear para +12 meses
  const finalValueAfter12Months = lastHistorical + (avgMonthlyIncrease * 12);

  return {
    monthlyIncrease: avgMonthlyIncrease,
    finalValueAfter12Months: Math.max(finalValueAfter12Months, 0), // evita negativos irreais
    hasEnoughData: true
  };
}, [projections, baseData]);

  // Último valor projetado (para o card de baixo)
  const lastProjectedValue = useMemo(() => {
    if (!projections || projections.length === 0) return 0
    const lastItem = projections[projections.length - 1]
    return lastItem.projected_payroll || lastItem.projected || lastItem.value || lastItem.total || 0
  }, [projections])

  // Mostrar detalhes da regra
  const handleShowRuleDetails = (impact) => {
    setSelectedRuleDetails(impact)
    setShowRuleDetailsModal(true)
  }

  // Custom Tooltip for projections with % change
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null

    const current = payload[0].payload
    const index = projections.findIndex(p => p.month === label)
    const prev = index > 0 ? projections[index - 1] : null

    const value = payload[0].value
    const prevValue = prev ? (prev.historical_payroll || prev.projected_payroll || prev.reference_point || 0) : 0
    const change = prevValue > 0 ? ((value - prevValue) / prevValue) * 100 : null

    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-xl min-w-[260px]">
        <p className="font-bold text-base mb-2">{label}</p>
        {payload.map((entry, i) => (
          <div key={i} className="flex items-center gap-2 mb-1.5">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-sm font-medium">{entry.name}:</span>
            <span className="font-bold ml-auto">
              R$ {Number(entry.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
          </div>
        ))}
        {change !== null && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-500">vs mês anterior:</span>
            <span className={`ml-2 font-bold ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change >= 0 ? '+' : ''}{change.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    )
  }

  // Exportar PNG
  const handleExportPNG = async () => {
  const element = document.getElementById('scenario-detail');
  if (!element) return toast.error('Elemento não encontrado');

  element.classList.add('export-hide-buttons');

  try {
    await new Promise(r => setTimeout(r, 200));

    const dataUrl = await htmlToImage.toPng(element, {
      quality: 1,
      pixelRatio: 2,
      backgroundColor: null,
      width: element.scrollWidth,
      height: element.scrollHeight,
      canvasWidth: element.scrollWidth * 2,
      canvasHeight: element.scrollHeight * 2,
      style: {
        transform: 'none',
        filter: 'none'
      }
    });

    const link = document.createElement('a');
    link.download = `cenario-${scenario?.name || 'detalhes'}.png`;
    link.href = dataUrl;
    link.click();

    toast.success('PNG exportado!');
  } catch (err) {
    console.error(err);
    toast.error('Erro ao exportar');
  } finally {
    element.classList.remove('export-hide-buttons');
  }
};

  // Exportar PDF
 const handleExportPDF = () => {
  const element = document.getElementById('scenario-detail');
  element.classList.add('export-hide-buttons');

  html2pdf()
    .from(element)
    .set({
      margin: 1,
      filename: `cenario-${scenario?.name || 'detalhes'}.pdf`,
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    })
    .save()
    .then(() => {
      toast.success('PDF gerado!');
    })
    .catch(err => {
      console.error(err);
      toast.error('Erro ao gerar PDF');
    })
    .finally(() => {
      element.classList.remove('export-hide-buttons');
    });
}; 

  if (loading) return <Loading />
  if (error) return <div className="p-4 text-red-600">{error}</div>
  if (!scenario) return <div className="p-4">Cenário não encontrado</div>

  const RuleConditions = ({ conditions = [] }) => {
    if (!conditions?.length) {
      return (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic mt-1">
          Sem condições específicas
        </p>
      );
    }

    return (
      <div className="mt-2 text-xs">
        <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Condições:</p>
        <div className="space-y-1 pl-2 border-l-2 border-gray-300 dark:border-gray-600">
          {conditions.map((cond, index) => (
            <div key={index} className="text-gray-600 dark:text-gray-400">
              • {cond.dimension === 'grupo' ? 'Grupo' : cond.dimension}:{' '}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                {cond.value}
              </span>
              {' → '}
              <span className="text-green-600 dark:text-green-400 font-medium">
                +{cond.increase_value}%
              </span>
              {' '}
              <span className="text-gray-500 dark:text-gray-500">
                ({cond.calculation_base === 'gross' ? 'bruto' : 'líquido'})
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div id="scenario-detail" className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4 md:p-6">
      <style>{`
        .export-hide-buttons .back-button,
        .export-hide-buttons .export-buttons {
          display: none !important;
        }
        .export-pdf .lucide svg {
  display: none !important;
}
      `}</style>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/cenarios')} className="flex items-center gap-2 back-button">
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{scenario.name}</h1>
              <p className="text-gray-600 dark:text-gray-400">{scenario.description}</p>
            </div>
          </div>
          <div className="flex gap-2 export-buttons">
            <Button onClick={handleExportPNG} variant="outline" className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              PNG
            </Button>
            <Button onClick={handleExportPDF} variant="outline" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              PDF
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-4">
          {scenario.status === 'active' && <Badge variant="success">Ativo</Badge>}
          {scenario.status === 'draft' && <Badge variant="warning">Rascunho</Badge>}
          {scenario.status === 'archived' && <Badge variant="secondary">Arquivado</Badge>}
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Base: {scenario.calculation_base === 'gross' ? 'Bruto' : 'Líquido'} | Mês: {scenario.reference_month || '—'}
          </span>
        </div>
      </motion.div>

      {/* Resumo da Simulação */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Resumo da Simulação</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Configurações</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Base de Cálculo:</span>
                <span className="font-medium">{scenario.calculation_base === 'gross' ? 'Salário Bruto' : 'Salário Líquido'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Mês de Referência:</span>
                <span className="font-medium">{scenario.reference_month || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Filtros Ativos:</span>
                <span className="font-medium">{Object.keys(scenario.layout_json?.filters || {}).length}</span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Impacto Calculado</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Funcionários Base:</span>
                <span className="font-medium">{baseData?.total_employees?.toLocaleString('pt-BR') || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Folha Base:</span>
                <span className="font-medium">R$ {baseData?.payroll_total?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Aumento Total:</span>
                <span className="font-medium text-green-600">
                  +R$ {calculatedTotals?.total_increase?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Salário Bruto:</span>
                <span className="font-medium text-purple-600">
                  R$ {calculatedTotals?.new_payroll?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Variação:</span>
                <span className="font-medium text-orange-600">
                  +{calculatedTotals?.percentage_increase?.toFixed(2) || '—'}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Regras Aplicadas - Detalhado */}
      <div className="card bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-800 shadow-xl mb-6">
  <div className="card-body">
    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
      <TrendingUp className="w-5 h-5 text-green-600" />
      Regras Aplicadas ({appliedRules.length})
    </h3>
    
    <div className="space-y-3">
      {appliedRules.map((impact, idx) => (
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="primary">#{idx + 1}</Badge>
                <h4 className="font-semibold">{impact.rule?.name || 'Regra não encontrada'}</h4>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {impact.rule?.description || 'Sem descrição'}
              </p>

              <RuleConditions conditions={impact.rule?.conditions_json || []} />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-4">
                <div>
                  <p className="text-gray-500 text-xs">Proporção</p>
                  <p className="font-semibold">{impact.proportion?.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Funcionários Afetados</p>
                  <p className="font-semibold">{impact.affected_employees?.toLocaleString('pt-BR')}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Aumento Estimado</p>
                  <p className="font-semibold text-green-600">
                    +R$ {impact.estimated_increase?.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Impacto na Folha</p>
                  <p className="font-semibold text-orange-600">
                    +{impact.percentage_impact?.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleShowRuleDetails(impact)}
                className="text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              >
                <BarChart3 className="w-4 h-4 mr-1" />
                Detalhes
              </Button>
            </div>
          </div>
        </motion.div>
      ))}
    </div>

          {/* Resumo Total */}
          {calculatedTotals && (
            <div className="mt-4 p-4 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-lg border-2 border-purple-300 dark:border-purple-700">
              <h4 className="font-bold text-lg mb-3 text-purple-900 dark:text-purple-200 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Resumo da Simulação
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Folha Atual</p>
                  <p className="text-lg font-bold">
                    R$ {calculatedTotals.current_payroll.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Aumento Total</p>
                  <p className="text-lg font-bold text-green-600">
                    +R$ {calculatedTotals.total_increase.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Salário Bruto</p>
                  <p className="text-lg font-bold text-purple-600">
                    R$ {calculatedTotals.new_payroll.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Variação</p>
                  <p className="text-lg font-bold text-orange-600">
                    +{calculatedTotals.percentage_increase?.toFixed(2) || '—'}%
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resumo das Projeções + Gráfico */}
      <div className="card bg-white dark:bg-gray-800 shadow-xl mb-6">
        <div className="card-body">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Projeções de 12 Meses
          </h3>

          {projections.length > 0 ? (
            <>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border-2 border-blue-200 dark:border-blue-800 p-6 md:p-8 mb-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-blue-900 dark:text-blue-200">
                      Resumo das Projeções
                    </h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Impacto médio mensal baseado nas regras aplicadas
                    </p>
                  </div>
                </div>

                <div className="space-y-6 md:space-y-8">
                  {/* Primeiro grupo */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-7">
                    <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-5 shadow-lg text-white border border-purple-400/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Calculator className="w-5 h-5 text-purple-100" />
                        <span className="text-xs font-medium uppercase tracking-wide text-purple-100">
                          Salário Bruto
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-white">
                        R${calculatedTotals.new_payroll.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-400">
                          Aumento Médio Mensal
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-green-600">
                        R$ {(appliedRules.reduce((sum, rule) => sum + (rule.estimated_increase || 0), 0) / 12).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">por mês</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2 mb-2">
                        <BarChart3 className="w-5 h-5 text-blue-600" />
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-400">
                          Tendência Base
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        R$ {baseData?.payroll_total?.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || '0'}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">folha atual</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-orange-600" />
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-400">
                          Percentual Médio
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-orange-600">
                        {calculatedTotals?.percentage_increase?.toFixed(0) || '0'}%
                      </div>
                      <p className="text-xs text-gray-500 mt-1">de aumento</p>
                    </div>
                  </div>

                  {/* Segundo grupo - com aumento médio anual no primeiro card */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-7">
                    {/* Card 1 - Agora Aumento Médio Anual */}
                    <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-5 shadow-lg text-white border border-purple-400/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Calculator className="w-5 h-5 text-purple-100" />
                        <span className="text-xs font-medium uppercase tracking-wide text-purple-100">
                          Valor Final da projeção
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-white">
                        R$ {lastProjectedValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-400">
                          Aumento Total Anual
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-green-600">
                        R$ {appliedRules.reduce((sum, rule) => sum + (rule.estimated_increase || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">por ano</p>
                    </div>

                    {/* Card 2 - Tendência Base */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-md border border-gray-200 dark:border-gray-700">
                     <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-400">
                          Aumento Médio Mensal
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        <p className={`text-2xl font-bold ${projectionWithoutRules.monthlyIncrease >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {projectionWithoutRules.monthlyIncrease >= 0 ? '+' : ''}
                          R$ {Math.abs(Math.round(projectionWithoutRules.monthlyIncrease)).toLocaleString('pt-BR')}
                          {projectionWithoutRules.monthlyIncrease >= 0 ? '' : ' ↓'}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Sem a regra</p>
                    </div>

                    {/* Card 3 - Percentual Médio */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-400">
                          Aumento Total Anual 
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-orange-600">
                        <p className="text-2xl font-bold text-purple-600">
                          R$ {Math.round(projectionWithoutRules.finalValueAfter12Months).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Sem a regra</p>
                    </div>
                    
                  </div>
                </div>
              </div>

              {/* Gráfico */}
              <div className="h-[400px] md:h-[500px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={projections} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                    <XAxis 
                      dataKey="month" 
                      angle={-45} 
                      textAnchor="end" 
                      height={80}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis 
                      tickFormatter={(v) => `R$ ${(v / 1000000).toFixed(1)}M`}
                      width={80}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ paddingTop: 20 }} />
                    <Line 
                      type="monotone" 
                      dataKey="historical_payroll" 
                      stroke="#6B7280" 
                      name="Histórico" 
                      strokeWidth={2.5} 
                      dot={{ r: 4 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="reference_point" 
                      stroke="#10B981" 
                      name="Mês Base + Aumento" 
                      strokeWidth={0} 
                      dot={{ r: 8, fill: "#10B981", stroke: "#fff", strokeWidth: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="projected_payroll" 
                      stroke="#DC2626" 
                      name="Projeção" 
                      strokeWidth={2.5} 
                      strokeDasharray="5 5"
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
             {/* Tabela estilo Excel - abaixo do gráfico */}
            <div className="mt-10 bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="p-5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Valores Mensais Detalhados (Histórico + Projeção)
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Valores em R$ — Histórico (até mês base) e projeção (após mês base)
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border-collapse table-fixed">
                  <thead className="bg-gray-100 dark:bg-gray-900/70">
                    <tr>
                      <th className="w-40 px-6 py-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider sticky left-0 bg-gray-100 dark:bg-gray-900/70 z-10 border-r border-gray-300 dark:border-gray-600">
                        Mês / Ano
                      </th>
                      {(() => {
                        const years = [...new Set(
                          projections
                            .map(p => {
                              const match = p.month?.match(/(\d{4})$/);
                              return match ? match[1] : null;
                            })
                            .filter(Boolean)
                        )].sort((a, b) => a.localeCompare(b));

                        return years.map(year => (
                          <th key={year} className="px-6 py-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider min-w-[160px] border-r border-gray-300 dark:border-gray-600 last:border-r-0">
                            {year}
                          </th>
                        ));
                      })()}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {[
                    { label: 'Jan', num: '01' },
                    { label: 'Fev', num: '02' },
                    { label: 'Mar', num: '03' },
                    { label: 'Abr', num: '04' },
                    { label: 'Mai', num: '05' },
                    { label: 'Jun', num: '06' },
                    { label: 'Jul', num: '07' },
                    { label: 'Ago', num: '08' },
                    { label: 'Set', num: '09' },
                    { label: 'Out', num: '10' },
                    { label: 'Nov', num: '11' },
                    { label: 'Dez', num: '12' }
                  ].map(({ label, num }) => (
                    <tr key={label} className="hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                      <td className="w-40 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 sticky left-0 bg-white dark:bg-gray-800 z-10 border-r border-gray-300 dark:border-gray-600 text-center">
                        {label}
                      </td>
                      {(() => {
                        const years = [...new Set(
                          projections
                            .map(p => {
                              const match = p.month?.match(/(\d{4})$/);
                              return match ? match[1] : null;
                            })
                            .filter(Boolean)
                        )].sort((a, b) => a.localeCompare(b));

                        return years.map(year => {
                          const monthPattern = new RegExp(`^${label}\\..*de ${year}$`, 'i');
                          const entry = projections.find(p => monthPattern.test(p.month));
                          let value = '-';
                          let cellClass = 'text-center text-sm text-gray-600 dark:text-gray-400 font-normal';

                          if (entry) {
                            const rawVal = entry.projected_payroll || entry.reference_point || entry.historical_payroll || 0;
                            if (rawVal > 0) {
                              value = `R$ ${(rawVal / 1000000).toFixed(1)}M`;

                              const isProjection = entry.projected_payroll && entry.projected_payroll > 0;
                              cellClass = isProjection
                                ? 'text-center text-sm font-semibold text-blue-600 dark:text-blue-400'  // cor para projeção (azul)
                                : 'text-center text-sm font-semibold text-gray-900 dark:text-white';    // cor para histórico (preto/branco)
                            }
                          }

                          return (
                            <td
                              key={`${year}-${num}`}
                              className={`px-6 py-4 whitespace-nowrap border-r border-gray-200 dark:border-gray-700 last:border-r-0 ${cellClass}`}
                            >
                              {value}
                            </td>
                          );
                        });
                      })()}
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>

              <div className="p-4 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 text-center md:text-left">
                Legenda: Valores em milhões (M). '-' = sem dado para esse mês/ano. Fonte: projeções do cenário carregado em {new Date().toLocaleDateString('pt-BR')}
              </div>
            </div>
            </>
          ) : (
            <div className="text-center py-12">
              <BarChart3 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Nenhuma projeção disponível
              </h3>
            </div>
          )}
        </div>
      </div>
      {/* Modal de detalhes da regra */}
      <Modal
        isOpen={showRuleDetailsModal}
        onClose={() => setShowRuleDetailsModal(false)}
        title={`Detalhes da Regra: ${selectedRuleDetails?.rule?.name || ''}`}
        size="lg"
      >
        {selectedRuleDetails && (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h4 className="font-semibold mb-2">Informações Gerais</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Tipo de Aumento:</span>
                  <p className="font-medium">Percentual</p>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Valor:</span>
                  <p className="font-medium">Calculado</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <h4 className="font-semibold mb-2 text-blue-900 dark:text-blue-200">Resumo do Impacto</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Total Funcionários:</span>
                  <p className="font-medium">{selectedRuleDetails.affected_employees?.toLocaleString('pt-BR')}</p>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Folha Afetada:</span>
                  <p className="font-medium">R$ {selectedRuleDetails.affected_payroll?.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Aumento Total:</span>
                  <p className="font-medium text-green-600">
                    +R$ {selectedRuleDetails.estimated_increase?.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">% na Folha:</span>
                  <p className="font-medium text-orange-600">
                    +{selectedRuleDetails.percentage_impact?.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}